import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { computed, unref, ref } from 'vue';

export function useFormHandler(validationSchema, onSubmitSuccess, customValidations = [], context = {}, initialValues = {}) {
  // Track if form has been submitted
  const formSubmitted = ref(false);
  // VeeValidate accepts computed refs directly - pass it through as-is
  // If it's already a computed, it will work. If it's a plain object, wrap it.
  const contextValue = computed(() => {
    const ctx = unref(context)
    // If context is already a computed that returns an object, call it
    if (typeof ctx === 'function') {
      const result = ctx()
      return typeof result === 'function' ? result() : result
    }
    return ctx
  });
  
  const { values, errors, handleSubmit, setFieldError, setFieldValue, setFieldTouched } = useForm({
    validationSchema,
    context: contextValue,
    validateOnMount: false,
    initialValues,
  });

  const customValidate = () => {
    let customErrors = {};
    // Ensure customValidations is always an array
    const validations = Array.isArray(customValidations) ? customValidations : [];
    validations.forEach(validation => {
      const error = validation(values); // Ensure that values are passed
      if (error) {
        customErrors = { ...customErrors, ...error };
      }
    });
    return customErrors;
  };

  const handleError = (errors) => {
    // Use nextTick to ensure DOM is updated with error messages
    setTimeout(() => {
      const formElements = document.querySelectorAll('form [name]'); // Get all elements in the form with a name attribute
      let firstErrorElement = null;

      // Iterate over the form elements and find the first one that has an error
      for (let i = 0; i < formElements.length; i++) {
        const name = formElements[i].getAttribute('name');
        if (errors[name]) {
          firstErrorElement = formElements[i];
          break;
        }
      }

      if (firstErrorElement) {
        // Check if we're in a modal (fixed position container with overflow)
        const modalContainer = firstErrorElement.closest('[class*="overflow-y-auto"], [class*="overflow-auto"]');
        
        if (modalContainer && modalContainer !== document.body) {
          // Scroll within the modal container
          const yOffset = -20;
          const containerRect = modalContainer.getBoundingClientRect();
          const elementRect = firstErrorElement.getBoundingClientRect();
          const scrollTop = modalContainer.scrollTop || 0;
          const relativeTop = elementRect.top - containerRect.top + scrollTop;
          modalContainer.scrollTo({ top: relativeTop + yOffset, behavior: 'smooth' });
        } else {
          // Regular page scroll
          const yOffset = -200;
          const y = firstErrorElement.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  const onSubmit = handleSubmit(
      (values) => {
        formSubmitted.value = true;
        const customErrors = customValidate();
        if (Object.keys(customErrors).length === 0) {
          onSubmitSuccess(values);
        } else {
          // Mark all fields with errors as touched so errors display
          Object.keys(customErrors).forEach((key) => {
            setFieldError(key, customErrors[key]);
            setFieldTouched(key, true);
          });
          handleError(customErrors);
        }
      },
      ({ errors }) => {
        formSubmitted.value = true;
        // Mark all fields with errors as touched so errors display
        Object.keys(errors).forEach((key) => {
          setFieldTouched(key, true);
        });
        handleError(errors);
      },
  );

  return {
    values,
    errors,
    onSubmit,
    setFieldValue,
    setFieldError,
    formSubmitted
  };
}
